import { AdminLayout } from "./AdminLayout";
import { useState, useEffect } from "react";
import { Copy, Check, AlertCircle, ExternalLink, ChevronDown } from "lucide-react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import { useParams } from "react-router-dom";

const PromptExecutionTracker = () => {
  const { promptPackId } = useParams();
  const [prompts, setPrompts] = useState([]);
  const [promptPack, setPromptPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    fetchPrompts();
  }, [promptPackId]);

  const fetchPrompts = async () => {
    try {
      const [promptsRes, packRes] = await Promise.all([
        axios.get(`${API}/springboard/prompt-packs/${promptPackId}/prompts`, { withCredentials: true }),
        axios.get(`${API}/springboard/prompt-packs/${promptPackId}`, { withCredentials: true })
      ]);
      setPrompts(promptsRes.data.prompts);
      setPromptPack(packRes.data);
    } catch (error) {
      toast.error('Failed to fetch prompts');
    } finally {
      setLoading(false);
    }
  };

  const copyPrompt = async (prompt) => {
    try {
      await navigator.clipboard.writeText(prompt.prompt_text);
      setCopiedId(prompt.prompt_id);
      toast.success('Prompt copied to clipboard!');
      
      // Auto-mark as PASTED
      await updateStatus(prompt.prompt_id, 'PASTED');
      
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const updateStatus = async (promptId, newStatus) => {
    // Check dependencies before allowing IMPLEMENTED or VERIFIED
    const prompt = prompts.find(p => p.prompt_id === promptId);
    
    if ((newStatus === 'IMPLEMENTED' || newStatus === 'VERIFIED') && prompt.dependencies.length > 0) {
      // Check if all dependencies are VERIFIED
      const allDepsVerified = prompt.dependencies.every(depId => {
        const dep = prompts.find(p => p.prompt_id === depId);
        return dep && dep.status === 'VERIFIED';
      });
      
      if (!allDepsVerified) {
        toast.error('Cannot mark as ' + newStatus + ' until all dependencies are VERIFIED');
        return;
      }
    }
    
    try {
      await axios.put(
        `${API}/springboard/prompts/${promptId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      
      // Update local state
      setPrompts(prompts.map(p => 
        p.prompt_id === promptId 
          ? { ...p, status: newStatus }
          : p
      ));
      
      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const updateTaskLink = async (promptId, taskLink) => {
    try {
      await axios.put(
        `${API}/springboard/prompts/${promptId}/task-link`,
        { task_link: taskLink },
        { withCredentials: true }
      );
      
      setPrompts(prompts.map(p => 
        p.prompt_id === promptId 
          ? { ...p, emergent_task_link: taskLink }
          : p
      ));
    } catch (error) {
      toast.error('Failed to save task link');
    }
  };

  const updateNotes = async (promptId, notes) => {
    try {
      await axios.put(
        `${API}/springboard/prompts/${promptId}/notes`,
        { notes: notes },
        { withCredentials: true }
      );
      
      setPrompts(prompts.map(p => 
        p.prompt_id === promptId 
          ? { ...p, verification_notes: notes }
          : p
      ));
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'DRAFT': 'bg-gray-500/20 text-gray-400',
      'READY': 'bg-blue-500/20 text-blue-400',
      'PASTED': 'bg-yellow-500/20 text-yellow-400',
      'IMPLEMENTED': 'bg-purple-500/20 text-purple-400',
      'VERIFIED': 'bg-green-500/20 text-green-400',
      'SKIPPED': 'bg-red-500/20 text-red-400'
    };
    return colors[status] || colors['DRAFT'];
  };

  const canAdvanceStatus = (prompt, targetStatus) => {
    if ((targetStatus === 'IMPLEMENTED' || targetStatus === 'VERIFIED') && prompt.dependencies.length > 0) {
      return prompt.dependencies.every(depId => {
        const dep = prompts.find(p => p.prompt_id === depId);
        return dep && dep.status === 'VERIFIED';
      });
    }
    return true;
  };

  // Group by phase
  const groupedPrompts = prompts.reduce((acc, prompt) => {
    if (!acc[prompt.phase]) acc[prompt.phase] = [];
    acc[prompt.phase].push(prompt);
    return acc;
  }, {});

  if (loading) {
    return (
      <AdminLayout title="Prompt Tracker">
        <div className="text-center py-12">Loading prompts...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Prompt Tracker">
      <div className="space-y-6" data-testid="prompt-tracker">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-xl font-bold">Execution Tracker</h2>
            <p className="text-sm text-[#8A8A8A]">
              {promptPack?.name || 'Prompt Pack'}
            </p>
          </div>
          <div className="text-sm">
            <span className="text-[#8A8A8A]">Progress: </span>
            <span className="text-[#5F8FB8] font-bold">
              {prompts.filter(p => p.status === 'VERIFIED').length} / {prompts.length}
            </span>
          </div>
        </div>

        {/* Prompts by Phase */}
        {Object.entries(groupedPrompts).sort().map(([phase, phasePrompts]) => (
          <div key={phase} className="space-y-3">
            <h3 className="text-base font-bold text-[#5F8FB8]">{phase}</h3>
            
            {phasePrompts.sort((a, b) => a.step_number - b.step_number).map((prompt) => {
              const hasDeps = prompt.dependencies.length > 0;
              const depsVerified = hasDeps ? prompt.dependencies.every(depId => {
                const dep = prompts.find(p => p.prompt_id === depId);
                return dep && dep.status === 'VERIFIED';
              }) : true;
              
              return (
                <div key={prompt.prompt_id} className="card-dashboard">
                  <div className="flex flex-col gap-4">
                    {/* Header Row */}
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-[#8A8A8A] text-xs font-mono">Step {prompt.step_number}</span>
                          <span className={`badge ${getStatusColor(prompt.status)} text-xs`}>
                            {prompt.status}
                          </span>
                          {!depsVerified && (
                            <span className="badge bg-orange-500/20 text-orange-400 text-xs flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Blocked
                            </span>
                          )}
                        </div>
                        <h4 className="font-medium">{prompt.title}</h4>
                      </div>
                      
                      {/* Copy Button */}
                      <button
                        onClick={() => copyPrompt(prompt)}
                        className="btn-secondary text-sm flex items-center gap-2"
                        data-testid={`copy-prompt-${prompt.step_number}`}
                      >
                        {copiedId === prompt.prompt_id ? (
                          <><Check className="w-4 h-4" /> Copied</>
                        ) : (
                          <><Copy className="w-4 h-4" /> Copy Prompt</>
                        )}
                      </button>
                    </div>

                    {/* Dependencies Warning */}
                    {hasDeps && !depsVerified && (
                      <div className="bg-orange-500/10 border border-orange-500/20 rounded p-3 text-sm">
                        <p className="text-orange-400 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Waiting for dependencies to complete
                        </p>
                      </div>
                    )}

                    {/* Controls Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {/* Status Dropdown */}
                      <div>
                        <label className="text-xs text-[#8A8A8A] mb-1 block">Status</label>
                        <select
                          value={prompt.status}
                          onChange={(e) => updateStatus(prompt.prompt_id, e.target.value)}
                          className="input-field text-sm"
                          data-testid={`status-${prompt.step_number}`}
                        >
                          <option value="DRAFT">Draft</option>
                          <option value="READY">Ready</option>
                          <option value="PASTED">Pasted</option>
                          <option 
                            value="IMPLEMENTED"
                            disabled={!depsVerified}
                          >
                            Implemented {!depsVerified ? '(Blocked)' : ''}
                          </option>
                          <option 
                            value="VERIFIED"
                            disabled={!depsVerified}
                          >
                            Verified {!depsVerified ? '(Blocked)' : ''}
                          </option>
                          <option value="SKIPPED">Skipped</option>
                        </select>
                      </div>

                      {/* Emergent Task Link */}
                      <div>
                        <label className="text-xs text-[#8A8A8A] mb-1 block">Emergent Task Link</label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={prompt.emergent_task_link || ''}
                            onChange={(e) => updateTaskLink(prompt.prompt_id, e.target.value)}
                            placeholder="https://..."
                            className="input-field text-sm flex-1"
                          />
                          {prompt.emergent_task_link && (
                            <a
                              href={prompt.emergent_task_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 hover:bg-[#2E2E2E] rounded"
                            >
                              <ExternalLink className="w-4 h-4 text-[#5F8FB8]" />
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Pasted Indicator */}
                      <div>
                        <label className="text-xs text-[#8A8A8A] mb-1 block">Pasted At</label>
                        <div className="text-sm text-[#8A8A8A]">
                          {prompt.pasted_at ? (
                            <span className="text-green-400">
                              ✓ {new Date(prompt.pasted_at).toLocaleDateString()}
                            </span>
                          ) : (
                            <span>Not pasted yet</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Notes Field */}
                    <div>
                      <label className="text-xs text-[#8A8A8A] mb-1 block">Notes</label>
                      <textarea
                        rows={2}
                        value={prompt.verification_notes || ''}
                        onChange={(e) => updateNotes(prompt.prompt_id, e.target.value)}
                        placeholder="Add notes about implementation, issues, or verification..."
                        className="input-field text-sm resize-none"
                      />
                    </div>

                    {/* Dependencies Display */}
                    {hasDeps && (
                      <div className="text-xs text-[#8A8A8A]">
                        Dependencies: {prompt.dependencies.map(depId => {
                          const dep = prompts.find(p => p.prompt_id === depId);
                          return dep ? `Step ${dep.step_number}` : 'Unknown';
                        }).join(', ')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </AdminLayout>
  );
};

export default PromptExecutionTracker;
